import { useCallback } from 'react';
import { generateVCard, generateContactCard, formatContactText, ShareContactData } from '../utils/contactShareUtils';
import { loadSharePreferences } from '../utils/sharePrefs';
import { useGroupContext } from '../lib/groups/context';

export interface UseContactSharingProps {
    formData: ShareContactData;
    onNotification: (message: string, type?: 'success' | 'error') => void;
    onClose: () => void;
}

export function useContactSharing({ formData, onNotification, onClose }: UseContactSharingProps) {
    const { activeGroupId } = useGroupContext();
    // Debug Web Share API capabilities
    const debugShareCapabilities = () => {
        console.log('🔍 Web Share API Debug Info:');
        console.log('navigator.share available:', !!navigator.share);
        console.log('navigator.canShare available:', !!navigator.canShare);
        console.log('navigator.clipboard available:', !!navigator.clipboard);
        console.log('HTTPS:', location.protocol === 'https:');
        console.log('localhost:', location.hostname === 'localhost');
        console.log('User Agent:', navigator.userAgent);

        if (navigator.canShare) {
            // Test different file types
            const testFiles = [
                new File(['test'], 'test.txt', { type: 'text/plain' }),
                new File(['test'], 'test.vcf', { type: 'text/vcard' }),
                new File(['test'], 'test.html', { type: 'text/html' })
            ];

            testFiles.forEach(file => {
                console.log(`Can share ${file.type}:`, navigator.canShare({ files: [file] }));
            });

            console.log('Can share text:', navigator.canShare({ text: 'test' }));
            console.log('Can share with title and text:', navigator.canShare({ title: 'test', text: 'test' }));
        }
    };

    // Primary sharing function - prioritizes native share dialog
    const shareContactNatively = useCallback(async () => {
        debugShareCapabilities();

        try {
            const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
            const shareText = formatContactText(formData, loadSharePreferences(activeGroupId));

            if (navigator.share) {
                try {
                    // ONLY use text - no URL parameter to avoid platforms ignoring the text
                    await navigator.share({
                        title: `Contact: ${contactName}`,
                        text: shareText
                    });
                    onNotification('Contact shared successfully!', 'success');
                    onClose();
                    return true;
                } catch (shareError) {
                    console.log('Native share error:', shareError);
                    if (shareError.name === 'AbortError') {
                        // User cancelled - don't show error
                        onClose();
                        return true;
                    }
                    return false;
                }
            }
            return false;
        } catch (error) {
            console.error('Error in native sharing:', error);
            return false;
        }
    }, [formData, onNotification, onClose]);

    const shareAsContactCard = useCallback(async () => {
        debugShareCapabilities();

        try {
            const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
            const htmlCard = generateContactCard(formData);

            // Create and download the HTML file directly
            // (more reliable than trying to share through Web Share API)
            const blob = new Blob([htmlCard], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `${contactName.replace(/[^a-zA-Z0-9]/g, '_')}_contact_card.html`;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);

            onNotification(`Contact card downloaded! You can now share this beautiful HTML file.`, 'success');
            onClose();

        } catch (error) {
            console.error('Error creating contact card:', error);
            onNotification('Unable to create contact card', 'error');
        }
    }, [formData, onNotification, onClose]);

    const shareAsVCard = useCallback(async () => {
        debugShareCapabilities();

        try {
            const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
            const vCardData = generateVCard(formData);
            const fileName = `${contactName.replace(/[^a-zA-Z0-9]/g, '_')}.vcf`;

            // Create vCard file
            const vCardBlob = new Blob([vCardData], { type: 'text/vcard' });
            const vCardFile = new File([vCardBlob], fileName, { type: 'text/vcard' });

            // FORCE use of Web Share API - no more downloads unless absolutely necessary
            if (navigator.share) {
                // First test if we can share this specific file type
                console.log('🧪 Testing vCard file sharing capability...');
                const canShareVCard = navigator.canShare ? navigator.canShare({ files: [vCardFile] }) : true;
                console.log('Can share vCard files:', canShareVCard);

                try {
                    // Try to share the vCard file first
                    console.log('🔄 Attempting to share vCard file...');
                    await navigator.share({
                        title: `Contact: ${contactName}`,
                        text: `Contact card for ${contactName}`,
                        files: [vCardFile]
                    });
                    onNotification('Contact card shared successfully!', 'success');
                    onClose();
                    return;
                } catch (shareError) {
                    console.log('❌ vCard file sharing failed:', shareError.name, shareError.message);
                    if (shareError.name === 'AbortError') {
                        onClose();
                        return;
                    }

                    // If file sharing fails, share as text instead
                    console.log('🔄 Falling back to text sharing...');
                    try {
                        const shareText = formatContactText(formData, loadSharePreferences(activeGroupId));
                        await navigator.share({
                            title: `Contact: ${contactName}`,
                            text: shareText
                        });
                        onNotification('Contact info shared successfully!', 'success');
                        onClose();
                        return;
                    } catch (textShareError) {
                        console.log('❌ Text sharing also failed:', textShareError.name, textShareError.message);
                        if (textShareError.name === 'AbortError') {
                            onClose();
                            return;
                        }
                    }
                }
            } else {
                console.log('❌ Web Share API not available');
            }            // Only download if Web Share API is completely unavailable
            console.log('⚠️ FALLBACK: Web Share API failed, downloading file instead');
            onNotification('Your browser/device does not support vCard sharing. File downloaded instead.', 'error');
            const vCardUrl = URL.createObjectURL(vCardBlob);
            const downloadLink = document.createElement('a');
            downloadLink.href = vCardUrl;
            downloadLink.download = fileName;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(vCardUrl);
            onClose();

        } catch (error) {
            console.error('Error creating vCard:', error);
            onNotification('Unable to create contact card', 'error');
        }
    }, [formData, onNotification, onClose]);

    const copyContactInfo = useCallback(async () => {
        try {
            const shareText = formatContactText(formData, loadSharePreferences(activeGroupId));

            // Copy to clipboard and show notification
            if (navigator.clipboard && navigator.clipboard.writeText) {
                try {
                    await navigator.clipboard.writeText(shareText);
                    onNotification('Contact info copied to clipboard!', 'success');
                } catch (clipboardError) {
                    console.log('Clipboard write failed, trying fallback');
                    // Fallback clipboard method
                    const textArea = document.createElement('textarea');
                    textArea.value = shareText;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    try {
                        document.execCommand('copy');
                        onNotification('Contact info copied to clipboard!', 'success');
                    } catch (execError) {
                        onNotification('Unable to copy to clipboard', 'error');
                    }
                    document.body.removeChild(textArea);
                }
            }

            onClose();
        } catch (error) {
            console.error('Error copying to clipboard:', error);
            onNotification('Unable to copy to clipboard', 'error');
        }
    }, [formData, onNotification, onClose]);

    return {
        shareAsContactCard,
        shareAsVCard,
        copyContactInfo,
        shareContactNatively
    };
}
